import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
    Box, Paper, Typography, Button, CircularProgress, Alert, Divider, Chip, Stack,
} from '@mui/material';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import axios from 'axios';

// Instância pública — o token da URL é a credencial, não há login aqui.
const apiPublica = axios.create({
    baseURL: process.env.REACT_APP_API_URL || '/api',
});

const dataHoraLonga = (iso) =>
    iso
        ? new Date(iso).toLocaleString('pt-BR', {
            weekday: 'long', day: '2-digit', month: 'long',
            hour: '2-digit', minute: '2-digit',
        })
        : '—';

const Linha = ({ rotulo, valor }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.75 }}>
        <Typography variant="body2" color="text.secondary">{rotulo}</Typography>
        <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'right' }}>{valor}</Typography>
    </Box>
);

/**
 * Página pública de confirmação de consulta.
 *
 * Mostra só o que o paciente precisa para reconhecer o compromisso — nomes,
 * data, modalidade. Nada de prontuário ou dado clínico.
 */
export default function ConfirmacaoConsulta() {
    const { token } = useParams();

    const [consulta, setConsulta] = useState(null);
    const [loading, setLoading] = useState(true);
    const [erro, setErro] = useState(null);
    const [enviando, setEnviando] = useState(false);

    useEffect(() => {
        apiPublica.get(`/public/confirmacao/${token}`)
            .then((r) => setConsulta(r.data))
            .catch(() => setErro('Link inválido ou expirado. Entre em contato com o consultório.'))
            .finally(() => setLoading(false));
    }, [token]);

    const confirmar = async () => {
        setEnviando(true);
        try {
            const { data } = await apiPublica.post(`/public/confirmacao/${token}`);
            setConsulta(data);
            setErro(null);
        } catch (e) {
            setErro(e?.response?.data?.message
                || 'Não foi possível confirmar. Entre em contato com o consultório.');
        } finally {
            setEnviando(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: { xs: 2, sm: 4 } }}>
            <Paper elevation={0} sx={{ maxWidth: 520, width: '100%', p: { xs: 3, sm: 4 }, borderRadius: 3, boxShadow: '0 2px 16px rgba(0,0,0,0.08)' }}>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                    <EventAvailableIcon sx={{ color: '#1ABB9C' }} />
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>Confirmação de consulta</Typography>
                </Stack>

                {erro && <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert>}

                {consulta && (
                    <>
                        {consulta.confirmada && (
                            <Alert icon={<CheckCircleIcon fontSize="inherit" />} severity="success" sx={{ mb: 2 }}>
                                Presença confirmada. Obrigado!
                            </Alert>
                        )}

                        <Divider sx={{ my: 2 }} />
                        <Linha rotulo="Paciente" valor={consulta.nomePaciente || '—'} />
                        <Linha rotulo="Profissional" valor={consulta.nomePsicologo || '—'} />
                        <Linha rotulo="Data e hora" valor={dataHoraLonga(consulta.dataHora)} />
                        <Linha rotulo="Duração" valor={consulta.duracao ? `${consulta.duracao} min` : '—'} />
                        <Linha
                            rotulo="Modalidade"
                            valor={<Chip size="small" variant="outlined" label={consulta.modalidade || '—'} />}
                        />
                        <Divider sx={{ my: 2 }} />

                        {/* Sem poder confirmar e sem estar confirmada: a consulta já teve
                            outro desfecho (cancelada, realizada, falta). */}
                        {!consulta.confirmada && !consulta.podeConfirmar && (
                            <Alert severity="warning">
                                Esta consulta não está mais aberta para confirmação. Entre em contato com o consultório.
                            </Alert>
                        )}

                        {!consulta.confirmada && consulta.podeConfirmar && (
                            <Button
                                fullWidth
                                size="large"
                                variant="contained"
                                loading={enviando}
                                onClick={confirmar}
                                sx={{ bgcolor: '#1ABB9C', '&:hover': { bgcolor: '#129A80' }, textTransform: 'none', fontWeight: 700, py: 1.25 }}
                            >
                                Confirmar presença
                            </Button>
                        )}

                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, textAlign: 'center' }}>
                            Precisa remarcar? Entre em contato com o consultório.
                        </Typography>
                    </>
                )}
            </Paper>
        </Box>
    );
}
